import { countStringLength } from '../../scripts/utils';
import TextField from '../text-field/text-field';

export default class EngravingSelector {
  constructor(element) {
    this.selectors = {
      select: '.EngravingItem__Select',
      selectLabel: '.EngravingItem__Header',
      selectError: '.EngravingItem__Error.Select__Error',
      options: '.ProductForm__Option--Engraving',
      engravingsWrapper: '.EngravingSelector__EngravingItems',
      engravingItem: '.EngravingItem:not(.EngravingItem--Select)',
      engravingLabel: '.EngravingItem__Header',
      engravingInput: '.EngravingItem__Input',
      charCount: '.EngravingItem__CurrentCount',
      error: '.EngravingItem__Error',
    };
    this.elements = {
      container: element,
      select: element.querySelector(this.selectors.select),
      selectLabel: element.querySelector(this.selectors.selectLabel),
      selectError: element.querySelector(this.selectors.selectError),
      options: element.querySelectorAll(this.selectors.options),
      engravingsWrapper: element.querySelector(
        this.selectors.engravingsWrapper,
      ),
      engravingItems: element.querySelectorAll(this.selectors.engravingItem),
      engravingInputs: element.querySelectorAll(this.selectors.engravingInput),
    };

    this.init();
  }

  init() {
    if (this.elements.select) {
      this.currentNumOfEngravings = parseInt(this.elements.select.value, 10);

      this.elements.select.addEventListener('change', ({ target }) => {
        this.currentNumOfEngravings = parseInt(target.value, 10);
        this.updateEngravingItems();
        // TODO: Why is this needed?
        this.elements.select.setAttribute('data-has-selected', 'true');
        this.elements.selectLabel.classList.remove(
          'EngravingItem__Header--Error',
        );
        this.elements.selectError.setAttribute('hidden', 'hidden');
      });
    } else {
      // The product uses multiple engravings and NOT an option to select the amount of engravings
      this.currentNumOfEngravings = this.elements.engravingItems.length;
    }

    this.elements.engravingInputs.forEach((engravingInput) => {
      const engravingWrapper = engravingInput.closest(
        this.selectors.engravingItem,
      );

      const textField = new TextField(engravingWrapper);

      textField.on('input', (target) => {
        const chars = countStringLength(target.value);

        if (chars >= 1) {
          this.hideErrorMessage(engravingWrapper);
          this.updateInputState({ engravingWrapper, active: true });
        } else {
          this.updateInputState({ engravingWrapper, active: false });
        }

        this.setCharAmount(engravingWrapper, chars);
      });
    });
  }

  updateInputState({ engravingWrapper, active }) {
    const inputEl = engravingWrapper.querySelector(
      this.selectors.engravingInput,
    );

    if (inputEl) {
      inputEl.classList.toggle('-Active', active);
    }
  }

  /**
   * Removes the `name` attribute from the specified input element.
   * Therefore, no line item properties will be set when somehow submitting this input.
   *
   * @param {HTMLInputElement} engravingInput - the engraving's input element
   */
  // eslint-disable-next-line class-methods-use-this
  removeLineItemProperty(engravingInput) {
    engravingInput.removeAttribute('name');
  }

  /**
   * Sets the `name` attribute of the input of the specified engraving item to the
   * correct line item property, which is stored in `data-property-name` on the input element.
   * @param {HTMLElement} engravingWrapper - the wrapper of the engraving item
   */
  setLineItemProperty(engravingWrapper) {
    const engravingInput = engravingWrapper.querySelector(
      this.selectors.engravingInput,
    );

    if (!engravingInput) return;

    const propertyName = engravingInput.getAttribute('data-property-name');

    engravingInput.setAttribute('name', propertyName);
  }

  setCharAmount(engravingWrapper, amount) {
    const charCountEl = engravingWrapper.querySelector(
      this.selectors.charCount,
    );

    if (charCountEl) charCountEl.innerHTML = amount;
  }

  hideErrorMessage(engravingWrapper) {
    const errorEl = engravingWrapper.querySelector(this.selectors.error);
    const labelEl = engravingWrapper.querySelector(
      this.selectors.engravingLabel,
    );

    errorEl?.setAttribute('hidden', 'hidden');
    labelEl?.classList.remove('EngravingItem__Header--Error');
  }

  /**
   * Iterates over all engraving items to hide / display the correct items
   * as determined by the `currentNumOfEngravings`. Also removes the value, error msgs,
   * character count, and the line item property on the now hidden engraving items.
   */
  updateEngravingItems() {
    this.elements.engravingItems.forEach((engravingItem) => {
      this.hideErrorMessage(engravingItem);

      if (
        // eslint-disable-next-line operator-linebreak
        parseInt(engravingItem.getAttribute('data-index'), 10) <=
        this.currentNumOfEngravings
      ) {
        engravingItem.removeAttribute('hidden');

        this.setLineItemProperty(engravingItem);
      } else {
        engravingItem.setAttribute('hidden', 'hidden');

        const inputEl = engravingItem.querySelector(
          this.selectors.engravingInput,
        );

        if (inputEl) {
          inputEl.value = '';
          this.removeLineItemProperty(inputEl);
        }

        this.updateInputState({
          engravingWrapper: engravingItem,
          active: false,
        });
        this.setCharAmount(engravingItem, 0);
      }
    });
  }
}
